import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Col, Dropdown, Form, FormLabel, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import BranchSelect from "../../../Custom/Main/Branch/BranchSelect";
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import InputDate from "../../../Forms/Element/InputDate";
import { errorAlert, warnAlert, warnAlertYesOrNo } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import { formatDateTime, formatDateToYYYYMMDD, getFirstDayOfCurrentMonthDate } from "../../../../../utils/DateUtils";
import Common from "../../../../../constants/Common";
import InputText from "../../../Forms/Element/InputText";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import { useBranch } from "../../../../../context/BranchContext";
import LockOpenIcon from "../../../Icon/LockOpenIcon";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import LockIcon from "../../../Icon/LockIcon";
import CashStatusSelect from "./CashStatusSelect";
import CashStatusType from "./CashStatusType";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import ModalPage from "../../../Common/Modal/ModalPage";
import CurrentBalanceCashFlow from "./CurrentBalanceCashFlow";
import ClosingCashData from "./ClosingCashData";
import { ApiError } from "../../../Common/ApiError";
import SaveIcon from "../../../Icon/SaveIcon";
import DetailIcon from "../../../Icon/DetailIcon";
import CashTransactionModal from "./CashTransactionModal";
import CashDetail from "./CashDetail";
import { isIntegrationCash, resolveCashOwner } from "./cashPresentation";
import EmployeeConsumptionModal from "../Consumption/EmployeeConsumptionModal";
import useMenuAccess from "../../../../../hooks/useMenuAccess";
import Module from "../../../../../constants/Module";
import "./CashHistory.css";

const CashHistory = () => {

    const { t } = useTranslation();

    const { selectedBranch } = useBranch();
    const navigate = useNavigate();

    // Estados do filtro
    const [startDate, setStartDate] = useState(getFirstDayOfCurrentMonthDate());
    const [endDate, setEndDate] = useState(new Date());
    const [user, setUser] = useState('');
    const [type, setType] = useState(null);
    const [branch, setBranch] = useState(null);
    const [showIntegrationCashes, setShowIntegrationCashes] = useState(true);
    const effectiveBranchId = selectedBranch?.id ?? branch?.value ?? null;

    // Estados da grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [showLoading, setShowLoading] = useState(false);
    // Caixa marcado com um clique na linha; o menu Ações do topo age sobre ele. Substitui o
    // radio, que só aparecia nos caixas abertos de balcão e não deixava marcar os demais nem
    // para ver o detalhe.
    const [selectedCashId, setSelectedCashId] = useState(null);
    // Área que conta como "dentro" para manter a seleção: filtros, barra de ações e tabela.
    const workspaceRef = useRef(null);

    // Estados de modal
    const [showCloseCashRegisterConfirmation, setShowCloseCashRegisterConfirmation] = useState(false);
    // Caixa escolhido no menu da linha para fechar. Antes era o marcado no radio, e a ação do
    // menu Ações do topo agia sobre a linha marcada, que o operador às vezes nem lembrava qual era.
    const [cashToClose, setCashToClose] = useState(null);
    // Contagem da gaveta e observação digitadas na confirmação; vão junto no fechamento.
    const [closingForm, setClosingForm] = useState({ countedCash: '', closingObs: '' });
    const [showCashDetail, setShowCashDetail] = useState(false);
    const [selectedDetailRow, setSelectedDetailRow] = useState(null);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [showConsumptionModal, setShowConsumptionModal] = useState(false);
    // O atalho de consumo segue a liberação do menu Consumo da equipe (plano e grupo): com o
    // menu bloqueado, o recurso não pode continuar disponível por aqui.
    const canRegisterConsumption = useMenuAccess(Module.CASH_FLOW, 'employee_consumption');

    // Estados para caixas em aberto fora do período
    const [openCashesOutsidePeriod, setOpenCashesOutsidePeriod] = useState([]);
    const [showOpenCashesAlert, setShowOpenCashesAlert] = useState(false);
    const [triggerSearch, setTriggerSearch] = useState(false);

    // Constrói os filtros da busca
    const buildFilters = useCallback(() => {
        const filters = [];

        // Filtro de data obrigatório
        filters.push(
            {
                field: "createdAt",
                value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
                operation: SearchOperation.GREATER_THAN_EQUAL
            },
            {
                field: "createdAt",
                value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
                operation: SearchOperation.LESS_THAN_EQUAL
            }
        );

        // Filtro de filial (contexto tem prioridade sobre seleção manual)
        if (selectedBranch !== null) {
            filters.push({
                field: "branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        } else if (branch) {
            filters.push({
                field: "branch.id",
                value: branch.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Filtro de usuário
        if (user) {
            filters.push({
                field: "openingUser.firstName",
                value: user,
                operation: SearchOperation.MATCH
            });
        }

        // Filtro de status
        if (type) {
            if (type.value === CashStatusType.OPENED) {
                filters.push({
                    field: "closingDate",
                    operation: SearchOperation.IS_NULL
                });
            } else if (type.value === CashStatusType.CLOSED) {
                filters.push({
                    field: "closingDate",
                    operation: SearchOperation.IS_NOT_NULL
                });
            }
        }

        if (!showIntegrationCashes) {
            filters.push({
                field: "automatic",
                value: false,
                operation: SearchOperation.EQUAL
            });
        }

        return filters;
    }, [startDate, endDate, selectedBranch, branch, user, type, showIntegrationCashes]);

    // Função para buscar caixas em aberto fora do período
    const checkOpenCashesOutsidePeriod = useCallback(() => {
        const filters = [
            {
                field: "closingDate",
                operation: SearchOperation.IS_NULL
            },
            {
                // Caixas de integração são permanentes e não devem aparecer como
                // pendência operacional fora do período pesquisado.
                field: "automatic",
                value: false,
                operation: SearchOperation.EQUAL
            }
        ];

        // Adiciona filtro de filial se existir
        if (selectedBranch !== null) {
            filters.push({
                field: "branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        } else if (branch) {
            filters.push({
                field: "branch.id",
                value: branch.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Buscar caixas abertos antes da data inicial do filtro
        filters.push({
            field: "createdAt",
            value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
            operation: SearchOperation.LESS_THAN
        });

        const payload = {
            filters,
            page: 0,
            size: 100,
            orders: [{
                field: "createdAt",
                direction: "ASC"
            }]
        };

        axiosInstance.post(Api.cash_flow.cash + "/pageable", payload)
            .then((response) => {
                const openCashes = (response.data.content || []).filter((cash) => !isIntegrationCash(cash));
                setOpenCashesOutsidePeriod(openCashes);
                setShowOpenCashesAlert(openCashes.length > 0);
            })
            .catch(error => {
                console.error('Erro ao verificar caixas em aberto fora do período:', error);
            });
    }, [startDate, selectedBranch, branch]);

    // Função de busca otimizada
    const search = useCallback(() => {
        // Validações
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return;
        }
        if (!endDate) {
            warnAlert(t('validation.required.end_date'));
            return;
        }

        const payload = {
            filters: buildFilters(),
            page,
            size,
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        };

        setShowLoading(true);
        axiosInstance.post(Api.cash_flow.cash + "/pageable", payload)
            .then((response) => {
                const content = showIntegrationCashes
                    ? response.data.content
                    : response.data.content.filter((cash) => !isIntegrationCash(cash));
                setPageable(response.data);
                setRows(content);
                // Verifica caixas em aberto fora do período após busca principal
                checkOpenCashesOutsidePeriod();
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [startDate, endDate, buildFilters, page, size, t, checkOpenCashesOutsidePeriod, showIntegrationCashes]);

    // Navega para o formulário de novo caixa
    const redirectToNew = useCallback(() => {
        navigate(MenuConfig.cash_flow.cash_registers.route + "/new");
    }, [navigate]);

    // Clique na linha marca o caixa; clicar de novo na mesma linha desmarca.
    const toggleCashSelection = useCallback((id) => {
        setSelectedCashId((current) => (current === id ? null : id));
    }, []);

    /*
     * Clicar fora da área de trabalho desmarca o caixa. Sem isso, o operador saía da tabela
     * achando que tinha largado a linha, e a ação seguinte agia sobre um caixa que ele nem
     * lembrava ter marcado. Modais, alertas e menus ficam de fora da limpeza, porque são a
     * continuação do que foi iniciado com a linha marcada.
     */
    useEffect(() => {
        const clearSelectionOnOutsideClick = (event) => {
            if (!workspaceRef.current || workspaceRef.current.contains(event.target)) {
                return;
            }
            if (event.target.closest?.('.modal, .swal2-container, .dropdown-menu, .offcanvas')) {
                return;
            }
            setSelectedCashId(null);
        };

        document.addEventListener('mousedown', clearSelectionOnOutsideClick);
        return () => document.removeEventListener('mousedown', clearSelectionOnOutsideClick);
    }, []);

    const openCashDetail = useCallback((row) => {
        setSelectedDetailRow(row);
        setShowCashDetail(true);
    }, []);

    // Pede a confirmação do fechamento do caixa escolhido no menu da linha. O menu só oferece
    // a opção para caixa de balcão aberto; a checagem fica aqui também por segurança.
    const closeCashRegister = useCallback((cash) => {
        if (cash?.closingDate) {
            warnAlert(t('cash_flow.cash_already_closed'));
            return;
        }
        if (isIntegrationCash(cash)) {
            warnAlert(t('cash_flow.integration_cash_cannot_close'));
            return;
        }
        setCashToClose(cash);
        setClosingForm({ countedCash: '', closingObs: '' });
        setShowCloseCashRegisterConfirmation(true);
    }, [t]);

    // Confirma o fechamento do caixa
    const confirmCloseCashRegister = useCallback(() => {
        // Os dois campos são opcionais: vazio vai como nulo e o backend fecha como antes.
        axiosInstance.patch(`${Api.cash_flow.cash}/close/${cashToClose.id}`, {
            countedCash: closingForm.countedCash !== '' ? Number(closingForm.countedCash) : null,
            closingObs: closingForm.closingObs.trim() || null
        })
            .then(() => {
                search();
                setShowCloseCashRegisterConfirmation(false);
                setCashToClose(null);
                setSelectedCashId(null);
            })
            .catch(error => {
                setShowCloseCashRegisterConfirmation(true);
                ApiError(error);
            });
    }, [cashToClose, closingForm, search]);

    const askCloseCashRegister = () => {
        // Esconde o modal para que a confirmação receba o foco; cancelar preserva os dados.
        setShowCloseCashRegisterConfirmation(false);
        warnAlertYesOrNo(t('cash_flow.close_cash_question'), confirmCloseCashRegister,
            () => setShowCloseCashRegisterConfirmation(true));
    };

    const reopenCashRegister = useCallback((cash) => {
        if (!cash?.closingDate || isIntegrationCash(cash)) {
            warnAlert(t('cash_flow.reopen_cash_unavailable'));
            return;
        }
        warnAlertYesOrNo(t('cash_flow.reopen_cash_question'), () => {
            axiosInstance.patch(`${Api.cash_flow.cash}/reopen/${cash.id}`)
                .then(() => {
                    setSelectedCashId(null);
                    search();
                })
                .catch(error => ApiError(error));
        });
    }, [search, t]);

    /** Roda a ação do menu Ações com o caixa marcado, ou pede para marcar uma linha. */
    const withSelectedCash = (action) => {
        const cash = rows.find((row) => row.id === selectedCashId);
        if (!cash) {
            warnAlert(t('validation.required.row'));
            return;
        }
        action(cash);
    };

    // Renderiza uma linha da tabela
    const renderRow = useCallback((row, index) => {
        const isClosed = row.closingDate !== null;
        // A linha inteira marca o caixa, pelo clique ou pelo teclado (Enter ou espaço).
        return (
            <tr
                key={row.id || index}
                className={`align-middle cash-history__table-row grid-custom__row--selectable${selectedCashId === row.id ? ' grid-custom__row--selected' : ''}`}
                onClick={() => toggleCashSelection(row.id)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleCashSelection(row.id);
                    }
                }}
                tabIndex={0}
                aria-selected={selectedCashId === row.id}
            >
                <td>
                    <span className="fw-semibold">{resolveCashOwner(row, t)}</span>
                    {/* Caixa da integração: o saldo vem de cartão e PIX liquidados pelo
                        gateway, ninguém confere cédula e não há fechamento no fim do
                        expediente. Sem a marca, ele se confunde com um caixa de balcão na
                        listagem. */}
                    {isIntegrationCash(row) && (
                        <Badge bg="secondary" className="badge-sm ms-2" title={t('cash_flow.automatic_cash_hint')}>
                            <i className="fas fa-robot me-1"></i>
                            {t('cash_flow.automatic_cash')}
                        </Badge>
                    )}
                </td>
                <td>
                    <Badge bg="info" className="badge-lg">
                        {row.branch?.name || '-'}
                    </Badge>
                </td>
                <td className="text-muted">
                    {formatDateTime(row.createdAt)}
                </td>
                <td className="fw-semibold text-primary">
                    {t('currency_symbol')} {convertDoubleToStringPTBR(row.openingBalance)}
                </td>
                <td className="fw-semibold">
                    <CurrentBalanceCashFlow cash={row} />
                </td>
                <td>
                    {row.obs ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{row.obs}</Tooltip>}
                        >
                            <span className="text-muted" style={{ cursor: 'help' }}>
                                {row.obs.substring(0, 30)}{row.obs.length > 30 ? '...' : ''}
                            </span>
                        </OverlayTrigger>
                    ) : !row.closingObs && (
                        <span className="text-muted">-</span>
                    )}
                    {/* Observação do fechamento embaixo da abertura: a falta na gaveta ou a
                        ocorrência do turno aparece na lista, sem abrir o detalhe. */}
                    {row.closingObs && (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{row.closingObs}</Tooltip>}
                        >
                            <small className="d-block text-muted" style={{ cursor: 'help' }}>
                                {t('cash_flow.closing_obs_short')}: {row.closingObs.substring(0, 30)}{row.closingObs.length > 30 ? '...' : ''}
                            </small>
                        </OverlayTrigger>
                    )}
                </td>
                <td className="text-center">
                    {isIntegrationCash(row) && !isClosed ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('cash_flow.integration_cash_active_hint')}</Tooltip>}
                        >
                            <Badge bg="info" className="p-2">
                                <i className="fas fa-sync-alt me-1"></i> {t('cash_flow.active')}
                            </Badge>
                        </OverlayTrigger>
                    ) : isClosed ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('cash_flow.closed')}</Tooltip>}
                        >
                            <Badge bg="danger" className="p-2">
                                <LockIcon /> {t('cash_flow.closed')}
                            </Badge>
                        </OverlayTrigger>
                    ) : (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t('cash_flow.open')}</Tooltip>}
                        >
                            <Badge bg="success" className="p-2">
                                <LockOpenIcon /> {t('cash_flow.open')}
                            </Badge>
                        </OverlayTrigger>
                    )}
                </td>
                {/* O menu da linha é um atalho: abri-lo não marca nem desmarca a linha. */}
                <td className="cash-history__actions-cell" onClick={(event) => event.stopPropagation()}>
                    <Dropdown className="d-inline-flex">
                        <Dropdown.Toggle
                            variant="link"
                            className="cash-history__row-action i-false"
                            aria-label={t('actions')}
                        >
                            <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => openCashDetail(row)}>
                                <DetailIcon className="me-2" /> {t('detail')}
                            </Dropdown.Item>
                            {/* Fechar fica junto da linha: o caixa automático não fecha, e o
                                fechado não tem o que fechar. */}
                            {!isClosed && !isIntegrationCash(row) && (
                                <Dropdown.Item onClick={() => closeCashRegister(row)}>
                                    <LockIcon /> {t('cash_flow.close_cash_register')}
                                </Dropdown.Item>
                            )}
                            {isClosed && !isIntegrationCash(row) && (
                                <Dropdown.Item onClick={() => reopenCashRegister(row)}>
                                    <LockOpenIcon /> {t('cash_flow.reopen_cash_register')}
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
            </tr>
        );
    }, [openCashDetail, closeCashRegister, reopenCashRegister, selectedCashId, toggleCashSelection, t]);

    // Usa a filial efetivamente aplicada. Ao voltar o contexto para "Todas", o select
    // local ainda conserva a filial anterior por um render; quando ele é limpo pelo efeito
    // abaixo, effectiveBranchId muda para null e dispara a consulta sem filtro de filial.
    useEffect(() => {
        search();
    }, [page, size, effectiveBranchId]);

    // Executa busca quando o botão "Ver Caixas em Aberto" é clicado
    useEffect(() => {
        if (triggerSearch) {
            search();
            setTriggerSearch(false);
        }
    }, [triggerSearch, search]);

    // Sincroniza o campo branch com o contexto selectedBranch
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranch({ value: selectedBranch.id, label: selectedBranch.name });
        } else {
            setBranch(null);
        }
    }, [selectedBranch]);

    // Handler para o formulário de filtros
    const handleSearchSubmit = useCallback((e) => {
        e?.preventDefault();
        setPage(0); // Reseta para primeira página ao buscar
        search();
    }, [search]);

    // Handler para atalhos de data
    const handleQuickDateSelect = useCallback((startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }, []);

    // Handler para limpar filtros
    const handleClearFilters = useCallback(() => {
        setStartDate(getFirstDayOfCurrentMonthDate());
        setEndDate(new Date());
        setUser('');
        setType(null);
        setShowIntegrationCashes(true);
        if (selectedBranch === null) {
            setBranch(null);
        }
    }, [selectedBranch]);

    // Mostrar todos os caixas em aberto (incluindo fora do período)
    const showAllOpenCashes = useCallback(() => {
        setType({ value: CashStatusType.OPENED, label: t('cash_flow.open') });
        setStartDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
        setEndDate(new Date());
        setPage(0);
        setShowOpenCashesAlert(false);
        setTriggerSearch(true);
    }, [t]);

    const cashMetrics = useMemo(() => {
        // "Aberto" e "Fechado" são métricas operacionais de caixas de balcão. O caixa
        // automático possui ciclo permanente e é apresentado separadamente como ativo.
        const openCount = rows.filter((row) => !isIntegrationCash(row) && row.closingDate === null).length;
        const closedCount = rows.filter((row) => !isIntegrationCash(row) && row.closingDate !== null).length;

        return {
            total: pageable?.totalElements ?? rows.length,
            openCount,
            closedCount
        };
    }, [pageable, rows]);

    return (
        <div ref={workspaceRef}>
            <ModalPageEvent
                title={t('cash_flow.confirm_cash_register_closure')}
                show={showCloseCashRegisterConfirmation}
                setShow={setShowCloseCashRegisterConfirmation}
                child={cashToClose ? <ClosingCashData cashId={cashToClose.id} value={closingForm} onChange={setClosingForm} /> : null}
                size="md"
                centered={true}
                btnText={t('confirm')}
                btnOnClick={askCloseCashRegister}
                btnCloseOnClick={() => {
                    setShowCloseCashRegisterConfirmation(false);
                    setCashToClose(null);
                }}
                btnCloseText={t('cancel')}
            />

            <CashTransactionModal
                show={showTransactionModal}
                setShow={setShowTransactionModal}
                // O saldo do caixa muda com o lançamento, então a lista precisa refletir
                // o novo valor sem o operador ter que refazer a busca.
                onSaved={() => search()}
            />

            {/* Mesmo modal da tela Consumo da equipe. Consumo pago em dinheiro muda o saldo do
                caixa, então a lista recarrega como na Nova Movimentação. */}
            <EmployeeConsumptionModal
                show={showConsumptionModal}
                setShow={setShowConsumptionModal}
                onSaved={() => search()}
            />

            {/* key pelo caixa: abrir outro caixa recomeça o detalhe do zero. */}
            <ModalPage
                title={t('cash_flow.cash_detail')}
                show={showCashDetail}
                setShow={setShowCashDetail}
                size="lg"
                centered={true}
                child={selectedDetailRow && <CashDetail key={selectedDetailRow.id} cash={selectedDetailRow} />}
            />

            <div className="cash-history__filter-shell mb-4">
                <FilterAccordion
                    onSearch={handleSearchSubmit}
                    onClear={handleClearFilters}
                    showQuickDates={true}
                    onQuickDateSelect={handleQuickDateSelect}
                >
                    <Row className="g-3">
                        <Col lg={2} md={6}>
                            <FormLabel>{t('start_date')}</FormLabel>
                            <InputDate
                                onChange={setStartDate}
                                selected={startDate}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('end_date')}</FormLabel>
                            <InputDate
                                onChange={setEndDate}
                                selected={endDate}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('user')}</FormLabel>
                            <InputText
                                value={user}
                                onChange={(e) => setUser(e.target.value)}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('branch')}</FormLabel>
                            <BranchSelect
                                value={branch}
                                onChange={setBranch}
                                isDisabled={selectedBranch !== null}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('status')}</FormLabel>
                            <CashStatusSelect
                                value={type}
                                onChange={setType}
                                isClearable={true}
                            />
                        </Col>
                        <Col lg={3} md={6} className="d-flex align-items-end pb-2">
                            <Form.Check
                                type="switch"
                                id="show-integration-cashes"
                                checked={showIntegrationCashes}
                                onChange={(event) => setShowIntegrationCashes(event.target.checked)}
                                label={t('cash_flow.show_integration_cashes')}
                            />
                        </Col>
                    </Row>
                </FilterAccordion>
            </div>

            <div className="cash-history__metrics mb-4">
                <div className="cash-history__metric-card cash-history__metric-card--primary">
                    <div className="cash-history__metric-head">
                        <div>
                            <span className="cash-history__metric-label">{t('total')}</span>
                            <strong className="cash-history__metric-value text-primary">{cashMetrics.total}</strong>
                        </div>
                        <span className="cash-history__metric-icon">
                            <i className="fas fa-cash-register"></i>
                        </span>
                    </div>
                </div>
                <div className="cash-history__metric-card cash-history__metric-card--success">
                    <div className="cash-history__metric-head">
                        <div>
                            <span className="cash-history__metric-label">{t('cash_flow.open')}</span>
                            <strong className="cash-history__metric-value text-success">{cashMetrics.openCount}</strong>
                        </div>
                        <span className="cash-history__metric-icon">
                            <LockOpenIcon />
                        </span>
                    </div>
                </div>
                <div className="cash-history__metric-card cash-history__metric-card--danger">
                    <div className="cash-history__metric-head">
                        <div>
                            <span className="cash-history__metric-label">{t('cash_flow.closed')}</span>
                            <strong className="cash-history__metric-value text-danger">{cashMetrics.closedCount}</strong>
                        </div>
                        <span className="cash-history__metric-icon">
                            <LockIcon />
                        </span>
                    </div>
                </div>
            </div>

            <Alert variant="info" className="cash-history__integration-card mb-4">
                <div className="cash-history__integration-icon" aria-hidden="true">
                    <i className="fas fa-robot"></i>
                </div>
                <div>
                    <strong>{t('cash_flow.integration_cash_card_title')}</strong>
                    <p className="mb-1 mt-1">{t('cash_flow.integration_cash_card_description')}</p>
                    <small>{t('cash_flow.integration_cash_card_hint')}</small>
                </div>
            </Alert>

            {showOpenCashesAlert && openCashesOutsidePeriod.length > 0 && (
                <Alert
                    variant="warning"
                    className="cash-history__warning-alert d-flex align-items-center justify-content-between"
                    dismissible
                    onClose={() => setShowOpenCashesAlert(false)}
                >
                    <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-3 fs-4"></i>
                        <div>
                            <strong>{t('cash_flow.open_cashes_outside_period_title')}</strong>
                            <div className="mt-1">
                                {t('cash_flow.open_cashes_outside_period_message', { count: openCashesOutsidePeriod.length })}
                                <ul className="mb-0 mt-2">
                                    {openCashesOutsidePeriod.slice(0, 3).map((cash) => (
                                        <li key={cash.id}>
                                            <strong>{cash.branch.name}</strong> - {t('cash_flow.opened_by')} {resolveCashOwner(cash, t)} {t('at')} {formatDateTime(cash.createdAt)}
                                        </li>
                                    ))}
                                    {openCashesOutsidePeriod.length > 3 && (
                                        <li className="text-muted">
                                            {t('cash_flow.and_more', { count: openCashesOutsidePeriod.length - 3 })}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        size="sm"
                        className="ms-3 cash-history__warning-action"
                        onClick={showAllOpenCashes}
                    >
                        <LockOpenIcon /> {t('cash_flow.view_open_cashes')}
                    </Button>
                </Alert>
            )}

            <Row className="mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}>
                                    <SaveIcon /> {t('cash_flow.open_cash_register')}
                                </Dropdown.Item>
                                {/* Detalhe e fechamento agem sobre o caixa marcado na tabela. */}
                                <Dropdown.Item onClick={() => withSelectedCash(openCashDetail)}>
                                    <DetailIcon className="me-2" /> {t('cash_flow.cash_detail')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => withSelectedCash(closeCashRegister)}>
                                    <LockIcon /> {t('cash_flow.close_cash_register')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => withSelectedCash(reopenCashRegister)}>
                                    <LockOpenIcon /> {t('cash_flow.reopen_cash_register')}
                                </Dropdown.Item>
                                {/* Atalho para a saída de gaveta do dia a dia (o café, a água).
                                    Quem opera o caixa costuma ter acesso só a esta tela. */}
                                <Dropdown.Item onClick={() => setShowTransactionModal(true)}>
                                    <SaveIcon /> {t('cash_flow.new_transaction')}
                                </Dropdown.Item>
                                {/* Consumo do colaborador (refrigerante, água). Fica aqui porque
                                    é a recepção quem vê o colaborador pegar o produto, e ela
                                    costuma ter acesso só a esta tela. */}
                                {canRegisterConsumption && (
                                    <Dropdown.Item onClick={() => setShowConsumptionModal(true)}>
                                        <i className="fa-solid fa-mug-hot me-2" aria-hidden="true" />
                                        {t('cash_flow.new_employee_consumption')}
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>
                <table className="table table-list i-table style-1 mb-0 border-0 cash-history__table" id="guestTable-cash" style={{ minWidth: 1120 }}>
                    <thead>
                        <tr>
                            <th>{t("cash_flow.cash")}</th>
                            <th>{t("branch")}</th>
                            <th>{t("date_time")}</th>
                            <th>{t("cash_flow.opening_balance")}</th>
                            <th>{t("cash_flow.final_balance")}</th>
                            <th>{t("obs")}</th>
                            <th className="text-center">{t("status")}</th>
                            <th className="bg-none" style={{ width: 60 }} aria-label={t('actions')}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-cash-register fa-2x mb-3 d-block opacity-50"></i>
                                        <strong className="d-block mb-1">{t('cash_flow.empty_title')}</strong>
                                        <span>{t('cash_flow.empty_hint')}</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map(renderRow)}
                    </tbody>
                </table>
            </GridCustomBase>
        </div>
    );
}

export default CashHistory;
